import { EyeOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Popover,
  Row,
  Select,
  Spin,
  Tooltip,
  message
} from "antd";
import locale from 'antd/es/date-picker/locale/es_ES';
import moment from "moment";
import 'moment/locale/es';
import { Fragment, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HasAccess } from "@permify/react-role";
import { handlers } from "../../../../helpers/helpers";
import { consultationService } from "../../../../services/medicalConsultation";
import { getCompany, getDriverLicenses, getGlassesTypes } from "../../../../services/general";
import { getCookie } from "../../../../services/cookies";
import useRecommendationsModal from '../../../components/hooks/useRecommendationsModal';
import usePendingExams from "../../hooks/usePendingExams";
import ConclusionsModal from '../ConclusionsModal';
import SignButton from "../SignButton";

const { TextArea } = Input;
const { Option } = Select;
const { format } = require('rut.js');

const PsychologicalOne = ({
  id,
  form,
  disableForm,
  servicesList,
  serviceIndex,
  onFinishConsultation,
  generateReport,
  setIsEditingMode,
  onFormPopulated,
  patientFullName,
  countryCode,
  loadingConsultation,
  medicalConsultationDetails,
  unfinishedExams,
  approvedTextList,
  loading,
  listReproved,
  isUnableToChoose,
  hasToShowPopOver,
  setStatus,
  patientData,
  oldReport,
  oldReportCountry,
  countrySettings,
  showConfirmReprovingValues,
  setShowConfirmReprovingValues,
  validatedExams,
  generatedPrescriptions,
  hasDeferredExamPrescription,
}) => {
  const [loadingFetchs, setLoadingFetchs] = useState({
    licenses: false,
    glasses: false
  });
  const [driverLicenses, setDriversLicenses] = useState();
  const [glassesTypes, setGlassesTypes] = useState();
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [pendingReasons, setPendingReasons] = useState();
  const [companyData, setCompanyData] = useState({
    name: '',
    document_type: '',
    document_number: ''
  });
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [showReprovePopover, setShowReprovePopover] = useState(false);
  const [conclusionPosition, setConclusionPosition] = useState();
  const [resultConclusionText, setResultConclusionText] = useState("");
  const { t } = useTranslation();
  const recommendatiosModal = useRecommendationsModal({
    t,
    service: servicesList[serviceIndex],
    patientFullName,
    countryCode,
    conclusionPosition,
    setConclusionText: setResultConclusionText,
    medicalForm: form,
    countrySettings,
  });

  const fetchDriverLicenses = useCallback(async () => {
    setLoadingFetchs(loadingFetchs => { return { ...loadingFetchs, licenses: true } });
    const response = await getDriverLicenses();
    setDriversLicenses(response);
    setLoadingFetchs(loadingFetchs => { return { ...loadingFetchs, licenses: false } });
  }, []);

  const fetchGlassesTypes = useCallback(async () => {
    setLoadingFetchs(loadingFetchs => { return { ...loadingFetchs, glasses: true } });
    const response = await getGlassesTypes();
    setGlassesTypes(response);
    setLoadingFetchs(loadingFetchs => { return { ...loadingFetchs, glasses: false } });
  }, []);

  const fetchCompany = useCallback(async (params) => {
    const response = await getCompany(params);

    setCompanyData({
      name: response[0].business_name,
      document_type: response[0].document_type,
      document_number: response[0].document_type === 'rut'
        ? format(response[0].document_number)
        : response[0].document_number
    });
  }, []);

  const fetchPendingReasons = useCallback(async () => {
    setLoadingReasons(true);
    try {
      const response = await consultationService.getPendingReasons({ department_id: 2 });
      setPendingReasons(response);
    } catch (error) {
      console.error(error);
      message.error(t('medicalConsultation.consultationEdit.errorFetchingPendingReasons'));
    } finally {
      setLoadingReasons(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPendingReasons();
  }, [fetchPendingReasons]);

  const fetchBatteryValidityValues = useCallback(async (serviceId) => {
    const { dateEvaluation, validityEvaluation } = form.getFieldsValue(['dateEvaluation', 'validityEvaluation']);

    if (dateEvaluation && validityEvaluation) {
      return;
    }

    if (!countrySettings.medicalConsultation.autoFillBatteryValidity[countryCode]) {
      return;
    }

    try {
      const response = await consultationService.getBatteryValidity({ id: serviceId, admissionPatientId: id });
      const formatDate = (date) => moment(date, "YYYY-MM-DD");
      if (response && response.date_evaluation && response.validity_evaluation) {
        form.setFieldsValue({
          dateEvaluation: dateEvaluation || formatDate(response.date_evaluation),
          validityEvaluation: validityEvaluation || formatDate(response.validity_evaluation)
        });
      }
    } catch (error) {
      console.error(error);
      message.error(t('medicalConsultation.consultationEdit.errorFetchingBatteryValidity'));
    }
  }, [form, id, countryCode, countrySettings]);

  useEffect(() => {
    if (!driverLicenses && !glassesTypes) {
      fetchDriverLicenses();
      fetchGlassesTypes();
    }
  }, [fetchDriverLicenses, fetchGlassesTypes]);

  useEffect(() => {
    const selectedService = servicesList[serviceIndex];
    const formatDate = (date) => {
      return moment(date, "YYYY-MM-DD");
    };

    if (selectedService && selectedService.company_id) {
      fetchCompany({ id: selectedService.company_id });
    }

    if (medicalConsultationDetails && selectedService.medical_consultation_id) {
      const medicalConsultationStatus = approvedTextList?.find(approvedText =>
        approvedText.id === medicalConsultationDetails.consultation_result_id
      );

      setStatus(medicalConsultationStatus)

      form.setFieldsValue({
        driverLicense: medicalConsultationDetails['driver_licenses'],
        validDriverLicense: medicalConsultationDetails['valid_driver_license'] && formatDate(medicalConsultationDetails['valid_driver_license']),
        useOfGlasses: medicalConsultationDetails['use_of_glasses'],
        dateEvaluation: medicalConsultationDetails['date_evaluation'] && formatDate(medicalConsultationDetails['date_evaluation']),
        validityEvaluation: medicalConsultationDetails['validity_evaluation'] && formatDate(medicalConsultationDetails['validity_evaluation'], "YYYY-MM-DD"),
        testData: medicalConsultationDetails.test_data || selectedService.description,
        appliedEvaluation: medicalConsultationDetails.applied_evaluation || selectedService.exams_description,
        pendingReasons: medicalConsultationDetails["pending_reasons"]?.filter(reason => reason != null),
        conclusion: medicalConsultationDetails['conclusion'],
        indication: medicalConsultationDetails.indications,
        suggestion: medicalConsultationDetails.suggestions,
        status: medicalConsultationDetails.consultation_result_id,
      });
      setShowReprovePopover(medicalConsultationStatus?.status === "reproved");
    } else {
      form.setFieldsValue({
        testData: selectedService.description,
        appliedEvaluation: selectedService.exams_description
      });
    }

    if (selectedService && selectedService.id) {
      fetchBatteryValidityValues(selectedService.id);
    }

    form.setFieldsValue({
      id: selectedService["medical_consultation_id"] ? selectedService["medical_consultation_id"] : null,
      serviceId: selectedService["id"],
      serviceType: selectedService["type"],
      admissionPatientId: parseInt(id)
    });

    onFormPopulated?.(form.getFieldsValue());
  }, [serviceIndex, medicalConsultationDetails, servicesList, id, form, fetchCompany, approvedTextList, fetchBatteryValidityValues]);

  const saveText = (consultation) => {
    if (consultation && consultation["revaluation"]) {
      return "Editar Reevaluación";
    }

    if (consultation && consultation["state"] === "readyToSign") {
      return "Editar";
    }

    return "Guardar";
  };

  const downloadSignedReport = async (action, id, type) => {
    try {
      setLoadingDownload(true);
      const fileName = `${patientFullName}_${patientData.document_number}_${id}.pdf`
      await handlers.downloadSignedReport({
        id,
        type,
        countryCode,
        fileName,
        action,
      })
    } catch (error) {
      console.error(error);
      message.error({ content: "Error al descargar informe", style: { marginTop: "35vh", fontSize: 16, }, });
    } finally {
      setLoadingDownload(false);
    }
  };

  const downloadUnSignedReport = (id) => {
    setLoadingDownload(true);
    generateReport(id, undefined, false, false)
      .then((response) => {
        handlers.base642Open(response["pdf"]);
      })
      .finally(() => {
        setLoadingDownload(false);
      });
  };

  const fieldsMessages = {
    'driverLicense': t('medicalConsultations.psychologicalOneForm.driverLicenseMessage'),
    'validDriverLicense': t('medicalConsultations.psychologicalOneForm.validDriverLicenseMessage'),
    'useOfGlasses': t('medicalConsultations.psychologicalOneForm.useOfGlassesMessage'),
    'dateEvaluation': t('medicalConsultations.psychologicalOneForm.dateEvaluationMessage'),
    'validityEvaluation': t('medicalConsultations.psychologicalOneForm.validityEvaluationMessage'),
    'testData': t('medicalConsultations.psychologicalOneForm.testDataMessage'),
    'appliedEvaluation': t('medicalConsultations.psychologicalOneForm.appliedEvaluationMessage'),
    'status': t('medicalConsultations.psychologicalOneForm.approvedTextMessage'),
    'pendingReasons': t('medicalConsultations.healthForm.pendingReasonsMessage'),
    'conclusion': t('medicalConsultations.psychologicalOneForm.conclusionMessage')
  };

  const rulesForRequiredFields = (field) => {
    return {
      required: true,
      message: fieldsMessages[field],
    };
  };

  const validateCompletedReport = () => {
    form.validateFields()
      .then(value => {
        form.setFieldsValue({ sign: true });
      });
  };

  const pendingExams = usePendingExams(unfinishedExams, medicalConsultationDetails, disableForm);

  const handleOnSelect = (value) => {
    setShowReprovePopover(approvedTextList.find(approvedText => approvedText.id === value).status === "reproved");
  };

  const handleResultState = (selectedOption) => {
    const status = approvedTextList?.find((option) =>
      option.id === selectedOption
    );
    form.setFieldsValue({ pendingReasons: undefined });
    setStatus(status);

    let currentConclusion = "";

    if (countrySettings.medicalConsultation.useConclusionFromSelectedStatus[countryCode]) {
      currentConclusion = status.text || '';
    }
    if (countrySettings.medicalConsultation.usePredefinedPsychologyText[countryCode]) {
      let predefined = "";

      if (status.status.includes('approved')) {
        predefined = servicesList[serviceIndex].approved_text || '';

        if (countrySettings.medicalConsultation.applySecondaryStatusActions[countryCode]) {
          setShowReprovePopover(false);
          if (servicesList[serviceIndex].is_provider) {
            form.setFieldsValue({
              secondary_conclusion: servicesList[serviceIndex].secondary_approved_text
            });
          }
        }
      } else if (status.status === 'reproved') {
        predefined = servicesList[serviceIndex].reprobate_text || '';

        if (countrySettings.medicalConsultation.applySecondaryStatusActions[countryCode]) {
          setShowReprovePopover(servicesList[serviceIndex].type === "Battery");
          if (servicesList[serviceIndex].is_provider) {
            form.setFieldsValue({
              secondary_conclusion: servicesList[serviceIndex].secondary_disapproved_text
            });
          }
        }
      }

      if (predefined) {
        if (currentConclusion) {
          currentConclusion += `\n${predefined}`;
        } else {
          currentConclusion = predefined;
        }
      }
    }

    form.setFieldsValue({
      conclusion: currentConclusion
    });
  };


  const handleConclusionInput = (event) => {
    setResultConclusionText(event.target.value);
  };

  return (
    <Fragment>
      <Form form={form} onFinish={onFinishConsultation} onFieldsChange={() => setIsEditingMode(true)} size="large" layout="vertical">
        <Form.Item name="id" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="serviceId" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="serviceType" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="admissionPatientId" hidden>
          <Input type="hidden" />
        </Form.Item>

        <Row gutter={[0, 20]}>
          <Col lg={20} xs={24}>
            <Row>
              <p className="bold">{t('medicalConsultations.psychologicalOneForm.title')}</p>
              <Col lg={24} xs={24}>
                <Form.Item
                  name="driverLicense"
                  label={t('medicalConsultations.psychologicalOneForm.driverLicenseLabel')}
                  rules={[rulesForRequiredFields('driverLicense')]}
                >
                  {loadingFetchs['licenses'] ? (
                    <Row justify="center">
                      <Spin className="cmt-spin" />
                    </Row>
                  ) : (
                    <Select
                      id="licenseSelect"
                      mode="multiple"
                      placeholder={t('medicalConsultations.psychologicalOneForm.driverLicensePlaceholder')}
                      className="cmt-select"
                      disabled={disableForm}
                    >
                      {driverLicenses?.map((item, index) => {
                        return (
                          <Option key={index} value={item.name}>
                            {item.name}
                          </Option>
                        );
                      })}
                    </Select>
                  )}
                </Form.Item>
              </Col>
              <Col lg={24} xs={24}>
                <Form.Item
                  name="validDriverLicense"
                  label={t('medicalConsultations.psychologicalOneForm.validDriverLicenseLabel')}
                  rules={[rulesForRequiredFields('validDriverLicense')]}
                >
                  <DatePicker
                    className="date-picker"
                    locale={locale}
                    placeholder={t('medicalConsultations.psychologicalOneForm.validDriverLicensePlaceholder')}
                    style={{ width: '100%' }}
                    disabled={disableForm}
                    format="DD-MM-YYYY"
                  />
                </Form.Item>
              </Col>
              <Col lg={24} xs={24}>
                <Form.Item
                  name="useOfGlasses"
                  label={t('medicalConsultations.psychologicalOneForm.useOfGlassesLabel')}
                  rules={[rulesForRequiredFields('useOfGlasses')]}
                >
                  {loadingFetchs['glasses'] ? (
                    <Row justify="center">
                      <Spin className="cmt-spin" />
                    </Row>
                  ) : (
                    <Select
                      id="glassesSelect"
                      placeholder={t('medicalConsultations.psychologicalOneForm.useOfGlassesPlaceholder')}
                      className="cmt-select"
                      disabled={disableForm}
                    >
                      {glassesTypes?.map((item, index) => {
                        return (
                          <Option key={index} value={item.key}>
                            {item.text}
                          </Option>
                        );
                      })}
                    </Select>
                  )}
                </Form.Item>
              </Col>
              <Col lg={24} xs={24}>
                <Form.Item
                  name="dateEvaluation"
                  label={t('medicalConsultations.psychologicalOneForm.dateEvaluationLabel')}
                  rules={[rulesForRequiredFields('dateEvaluation')]}
                >
                  <DatePicker
                    className="date-picker"
                    locale={locale}
                    placeholder={t('medicalConsultations.psychologicalOneForm.dateEvaluationPlaceholder')}
                    style={{ width: '100%' }}
                    disabled={disableForm}
                    format="DD-MM-YYYY"
                  />
                </Form.Item>
              </Col>
              <Col lg={24} xs={24}>
                <Form.Item
                  name="validityEvaluation"
                  label={t('medicalConsultations.psychologicalOneForm.validityEvaluationLabel')}
                  rules={[rulesForRequiredFields('validityEvaluation')]}
                >
                  <DatePicker
                    className="date-picker"
                    locale={locale}
                    placeholder={t('medicalConsultations.psychologicalOneForm.validityEvaluationPlaceholder')}
                    style={{ width: '100%' }}
                    disabled={disableForm}
                    format="DD-MM-YYYY"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col lg={20} xs={24}>
            {companyData && (
              <Row>
                <p className="bold">{t('medicalConsultations.psychologicalOneForm.companyDataTitle')}</p>
                <Col lg={24} xs={24}>
                  <p>
                    {t('medicalConsultations.psychologicalOneForm.companyName')}: <span className="ml-10">{companyData && companyData['name']}</span>
                  </p>
                </Col>
                <Col lg={24} xs={24}>
                  <p>
                    {companyData?.document_type?.toLowerCase() === 'passport' ? 'Pasaporte' : companyData?.document_type?.toUpperCase()}:
                    <span className="ml-10">{companyData.document_number}</span>
                  </p>
                </Col>
              </Row>
            )}
          </Col>
          <Col lg={24} xs={24}>
            <Row>
              <p className="bold">{t('medicalConsultations.psychologicalOneForm.examData')}</p>
              <Col lg={24} xs={24}>
                <Form.Item name="testData" rules={[rulesForRequiredFields('testData')]}>
                  <TextArea className="cmt-input" style={{ height: 140 }} disabled={disableForm} />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col lg={24} xs={24}>
            <Row>
              <p className="bold">{t('medicalConsultations.psychologicalOneForm.appliedEvaluations')}</p>
              <Col lg={24} xs={24}>
                <Form.Item name="appliedEvaluation" rules={[rulesForRequiredFields('appliedEvaluation')]}>
                  <TextArea className="cmt-input" style={{ height: 140 }} disabled={disableForm} />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col lg={24} xs={24}>
            <Row>
              <Col lg={24} xs={24}>
                <p className="bold">{t('medicalConsultations.psychologicalOneForm.evaluation')}</p>
              </Col>
              <Col lg={20} xs={24}>
                <Form.Item
                  name="status"
                  label={t('medicalConsultations.psychologicalOneForm.approvedTextLabel')}
                  rules={[rulesForRequiredFields('status')]}
                >
                  {loading.text ? (
                    <Row justify="center">
                      <Spin className="cmt-spin" />
                    </Row>
                  ) : (
                    <Select
                      id="resultsSelect"
                      placeholder={t('medicalConsultations.psychologicalOneForm.approvedTextPlaceholder')}
                      className="cmt-select"
                      onSelect={handleOnSelect}
                      onChange={handleResultState}
                      disabled={disableForm && disableForm}
                    >
                      {approvedTextList?.map((option) =>
                        option.status === 'approved' && hasToShowPopOver() ? (
                          <Option key={option.id} value={option.id}>
                            <Popover
                              title={
                                <strong>
                                  {isUnableToChoose()
                                    ? t('medicalConsultations.consultationEdit.statusAlteredDisclaimerTitle')
                                    : t('medicalConsultations.consultationEdit.statusReprovedDisclaimerTitle')}
                                </strong>
                              }
                              trigger="click"
                              content={listReproved()}
                              overlayClassName="sidebar-tooltip"
                            >
                              {option.text}
                            </Popover>
                          </Option>
                        ) : (
                          <Option key={option.id} value={option.id}>
                            {option.text}
                          </Option>
                        ),
                      )}
                    </Select>
                  )}
                </Form.Item>
              </Col>
              <Col lg={24} xs={24}>
                {approvedTextList?.find((option) => option.id === form.getFieldValue('status'))?.status === 'pending' && (
                  <Form.Item name="pendingReasons" rules={[rulesForRequiredFields('pendingReasons')]}>
                    {loadingReasons ? (
                      <Spin className="cmt-spin" />
                    ) : (
                      <Select
                        className="cmt-select"
                        mode="multiple"
                        placeholder="Seleccione motivo/s"
                        style={{ width: '100%' }}
                        disabled={disableForm}
                      >
                        {pendingReasons?.map((item) => {
                          return (
                            <Option key={item.key} value={item.key}>
                              {item.description}
                            </Option>
                          );
                        })}
                      </Select>
                    )}
                  </Form.Item>
                )}
              </Col>
              <Col lg={24} xs={24}>
                <Form.Item
                  name="conclusion"
                  className="mb-0"
                  label={t('medicalConsultations.psychologicalOneForm.conclusionLabel')}
                  rules={[rulesForRequiredFields('conclusion')]}
                >
                  <TextArea
                    id="conclusionInput"
                    className={`cmt-input${resultConclusionText?.length >= 1600 && !oldReport && oldReportCountry ? '-error' : ''}`}
                    style={{ height: 140 }}
                    disabled={disableForm}
                    onChange={(event) => handleConclusionInput(event)}
                    maxLength={!oldReport && oldReportCountry && '1600'}
                  />
                </Form.Item>
              </Col>
              {!oldReport && oldReportCountry && (
                <Col lg={24} xs={24} className={`text-right conclusion-alert${resultConclusionText?.length >= 1600 ? '-error' : ''}`}>
                  Máximo 1.600 caracteres
                </Col>
              )}
              <Col lg={24} xs={24}>
                <Row gutter={15} className="mb-20">
                  <Col>
                    <Button
                      onClick={() =>
                        recommendatiosModal.showModal({
                          type: 'indication',
                          from: 'conclusions',
                        })
                      }
                      className="cmt-button secondary"
                      type="primary"
                      size="small"
                      disabled={disableForm}
                    >
                      {t('medicalConsultations.healthForm.indications')}
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      id="suggestionsButton"
                      onClick={() =>
                        recommendatiosModal.showModal({
                          type: 'suggestion',
                          from: 'conclusions',
                        })
                      }
                      className="cmt-button secondary"
                      type="primary"
                      size="small"
                      disabled={disableForm}
                    >
                      {t('medicalConsultations.healthForm.suggestions')}
                    </Button>
                  </Col>
                </Row>
              </Col>
              <Col lg={24} xs={24}>
                <Col lg={24} xs={24}>
                  <p className="bold">{t('medicalConsultations.healthForm.indications')}</p>
                </Col>

                <Col lg={24} xs={24}>
                  <Form.Item name="indication">
                    <TextArea className="cmt-input" style={{ height: 140 }} />
                  </Form.Item>
                </Col>
              </Col>
              <Col lg={24} xs={24}>
                <Col lg={24} xs={24}>
                  <p className="bold">{t('medicalConsultations.healthForm.suggestions')}</p>
                </Col>
                <Col lg={24} xs={24}>
                  <Form.Item name="suggestion">
                    <TextArea className="cmt-input" style={{ height: 140 }} />
                  </Form.Item>
                </Col>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row className="mt-70" gutter={15}>
          <HasAccess roles={['super_usuario', 'equipo_psicologia', 'direccion_medica']} permissions="save-medical-consultation">
            <Col xxl={4} lg={6} xs={24}>
              <Form.Item name="save">
                <Button
                  id="saveButton"
                  loading={loadingConsultation.save}
                  htmlType="submit"
                  onClick={() => form.setFieldsValue({ save: true })}
                  className="cmt-button primary"
                  type="primary"
                  size="large"
                  disabled={disableForm && disableForm}
                >
                  {saveText(medicalConsultationDetails)}
                </Button>
              </Form.Item>
            </Col>
            {medicalConsultationDetails?.started_at && (
              <Fragment>
                <Col xxl={4} lg={6} xs={24}>
                  <Form.Item name="saveAndFinalize">
                    <Button
                      loading={loadingConsultation.saveAndFinalize}
                      htmlType="submit"
                      onClick={() => form.setFieldsValue({ saveAndFinalize: true })}
                      className="cmt-button primary outline p-0"
                      type="primary"
                      size="large"
                      disabled={disableForm && disableForm}
                    >
                      Guardar y finalizar
                    </Button>
                  </Form.Item>
                </Col>
                {medicalConsultationDetails?.done_at && (
                  <Col xxl={4} lg={6} xs={24}>
                    <Form.Item name="sign">
                      {pendingExams ? (
                        <Popover
                          title="Exámenes pendientes"
                          trigger="hover"
                          content={
                            <div>
                              {unfinishedExams.map((unfinishedExam) => (
                                <p>{unfinishedExam.name}</p>
                              ))}
                            </div>
                          }
                          overlayClassName="sidebar-tooltip"
                        >
                          <Button id="signButton" htmlType="submit" className="cmt-button disabled" type="primary" size="large" disabled>
                            Firmar
                          </Button>
                        </Popover>
                      ) : (
                        <SignButton
                          countryCode={countryCode}
                          medicalFormType="psychologicalOne"
                          t={t}
                          loadingConsultation={loadingConsultation}
                          validateCompletedReport={validateCompletedReport}
                          form={form}
                          isUnableToChoose={isUnableToChoose}
                          showConfirmReprovingValues={showConfirmReprovingValues}
                          setShowConfirmReprovingValues={setShowConfirmReprovingValues}
                          isCompanyAuditorSignerEnabled={() => { }}
                          approvedTextList={approvedTextList}
                          medicalConsultationDetails={medicalConsultationDetails}
                          roles={getCookie('roles')}
                          validatedExams={validatedExams}
                          servicesList={servicesList}
                          loading={loading}
                          generatedPrescriptions={generatedPrescriptions}
                          hasDeferredExamPrescription={hasDeferredExamPrescription}
                        />
                      )}
                    </Form.Item>
                  </Col>
                )}
              </Fragment>
            )}
          </HasAccess>
          {medicalConsultationDetails?.started_at && !pendingExams && medicalConsultationDetails.state !== 'signed' && (
            <Col xxl={1} lg={2} md={2} sm={2} xs={24}>
              <Tooltip title="Previsualizar Informe" color="#223A70">
                <Button
                  className="cmt-button secondary outline"
                  type="secondary"
                  icon={<EyeOutlined />}
                  size={'large'}
                  loading={loadingDownload}
                  onClick={() => downloadUnSignedReport(medicalConsultationDetails['id'])}
                />
              </Tooltip>
            </Col>
          )}
          {medicalConsultationDetails?.state === 'signed' && (
            <Fragment>
              <Col xxl={1} lg={2} md={2} sm={2} xs={24}>
                <Tooltip title="Descargar Informe" color="#223A70">
                  <Button
                    className="cmt-button secondary outline pl-5"
                    type="secondary"
                    icon={<span className="icon-system-download" />}
                    size={'large'}
                    loading={loadingDownload}
                    onClick={() => downloadSignedReport('download', medicalConsultationDetails['id'], 'signed')}
                  />
                </Tooltip>
              </Col>
              <Col xxl={1} lg={2} md={2} sm={2} xs={24}>
                <Tooltip title="Ver Informe Firmado" color="#223A70">
                  <Button
                    className="cmt-button secondary outline pl-5"
                    type="secondary"
                    icon={<EyeOutlined />}
                    size={'large'}
                    loading={loadingDownload}
                    onClick={() => downloadSignedReport('open', medicalConsultationDetails['id'], 'signed')}
                  />
                </Tooltip>
              </Col>
            </Fragment>
          )}
        </Row>
      </Form>
      <ConclusionsModal recommendatiosModal={recommendatiosModal} />
    </Fragment>
  );
};

export default PsychologicalOne;
